import { useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { Show, UserButton, useAuth } from "@clerk/react";
import { Flame, Users } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import LeadTable from "@/components/admin/LeadTable";
import LeadDetail from "@/components/admin/LeadDetail";
import BookingsList from "@/components/admin/BookingsList";
import PhotoManager from "@/components/admin/PhotoManager";

const TABS = [["leads", "Leads"], ["bookings", "Bookings"], ["photos", "Photos"]];

const Stat = ({ icon: Icon, label, value, testid }) => (
  <div className="bg-white border border-parchment-300 rounded-2xl p-5 sm:p-6">
    <Icon className="w-5 h-5 text-brass-700" />
    <p className="mt-3 font-serif text-4xl sm:text-5xl font-bold text-forest-800" data-testid={testid}>{value ?? "–"}</p>
    <p className="text-xs uppercase tracking-widest text-[#57645A] mt-1">{label}</p>
  </div>
);

function Dashboard() {
  const { getToken } = useAuth();
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState(null);
  const [bookings, setBookings] = useState(null);
  const [catalog, setCatalog] = useState(null);
  const [tab, setTab] = useState("leads");
  const [error, setError] = useState("");
  const [active, setActive] = useState(null);

  const load = useCallback(async () => {
    try {
      const headers = { Authorization: `Bearer ${await getToken()}` };
      const [s, l, b, c] = await Promise.all([api.get("/admin/stats", { headers }), api.get("/admin/leads", { headers }),
        api.get("/admin/bookings", { headers }), api.get("/catalog")]);
      setStats(s.data);
      setLeads(l.data);
      setBookings(b.data);
      setCatalog(c.data);
    } catch (e) {
      setError(errMsg(e, "Could not load leads."));
    }
  }, [getToken]);

  const onPhotoSaved = (kind, id, image) => {
    const key = kind === "course" ? "courses" : "products";
    setCatalog((c) => ({ ...c, [key]: c[key].map((it) => (it.id === id ? { ...it, image } : it)) }));
  };

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="min-h-screen bg-parchment" data-testid="admin-dashboard">
      <header className="bg-forest-800 text-parchment">
        <div className="max-w-6xl mx-auto px-5 h-16 flex items-center justify-between">
          <p className="font-serif text-2xl font-bold">HithAyu <span className="eyebrow !text-brass-300 ml-1">Admin</span></p>
          <UserButton />
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-5 py-8 sm:py-12">
        {error ? (
          <div className="bg-white border border-red-200 text-red-700 rounded-2xl p-6" data-testid="admin-error">{error}</div>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-4 max-w-xl">
              <Stat icon={Users} label="Total leads" value={stats?.total_leads} testid="stat-total-leads" />
              <Stat icon={Flame} label="Hot this week" value={stats?.hot_this_week} testid="stat-hot-week" />
            </div>
            <div className="mt-12 mb-5 flex gap-6 border-b border-parchment-300 overflow-x-auto">
              {TABS.map(([k, l]) => (
                <button key={k} onClick={() => setTab(k)} data-testid={`admin-tab-${k}`}
                  className={`pb-3 -mb-px font-serif text-2xl sm:text-3xl font-semibold border-b-2 transition-colors ${tab === k ? "border-brass-500 text-forest-800" : "border-transparent text-[#8A978E]"}`}>{l}</button>
              ))}
            </div>
            {tab === "leads" && <LeadTable leads={leads} onSelect={setActive} />}
            {tab === "bookings" && <BookingsList bookings={bookings} />}
            {tab === "photos" && <PhotoManager catalog={catalog} onSaved={onPhotoSaved} />}
          </>
        )}
      </main>
      <LeadDetail lead={active} onClose={() => setActive(null)} />
    </div>
  );
}

export default function Admin() {
  return (
    <>
      <Show when="signed-in"><Dashboard /></Show>
      <Show when="signed-out"><Navigate to="/sign-in" replace /></Show>
    </>
  );
}
